/**
 * Exercices reliés à une SKILLCARD, dans le bloc « Ma maîtrise » de sa page
 * publique (migration 0278) : titre, accès (migration 0690 : UN seul exercice
 * gratuit par carte, son essai désigné ; tout le reste — autres essais, parties
 * payantes, exercices non découpés — est verrouillé d'emblée et le clic mène à
 * la page d'achat), « jusqu'à N HORA » (questions
 * reliées × HORA par bonne réponse, plafonné à ce qui manque), la difficulté
 * (« Niveau 2 · ×1,5 HORA », migration 0295) et un bouton lecture (▶). Pour un
 * admin : générer un exercice standard par l'IA (10 QCM sur la carte, en
 * brouillon, à relire avant publication).
 */

import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Layers,
  Lock,
  Map as MapIcon,
  Pencil,
  Play,
  RotateCcw,
  Send,
  Sparkles,
} from 'lucide-react';
import { Button, MathText, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { exerciseDifficultyBadge, exerciseXpPotential } from '@jeraisonne/db/cards';
import {
  CARD_ADMIN_STATUS_LABELS,
  CARD_EXERCISE_TIER_LABELS,
  exercisesForStudentLevel,
  splitCardExercises,
  useCardAdminExercises,
  useSetCardExerciseStatus,
  useGenerateCardExercise,
  useGenerateCardExerciseSet,
  type CardExerciseResult,
  type CardExerciseSetItem,
  type CardExerciseSheetProgress,
  type PublicCardExercise,
} from '@/lib/cards/api';
import { AiModelSelect, CARD_DEFAULT_MODEL } from './ai-model-select';
import { autoSheetSummary } from '@/features/scratch-editor/auto-sheet-report';
import type { PipelineModelId } from '@/lib/admin/pipeline-models';
import { skillcardBuyPath } from '@/lib/skillcards/purchase';
import { exerciseAttemptBadge, type ExerciseAttemptSummary } from '@/lib/cards/exercise-attempts';

const LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  SCHOOL_LEVELS.map((l) => [l.value, l.label]),
);

const DIFFICULTY_TONES: Record<1 | 2 | 3, string> = {
  1: 'bg-ink-100 text-ink-700',
  2: 'bg-amber-50 text-amber-700',
  3: 'bg-error-50 text-error-700',
};

/** Pastilles lisibles sur le fond sombre teinté d'une carte. */
const THEMED_DIFFICULTY_TONES: Record<1 | 2 | 3, string> = {
  1: 'bg-white/15 text-white',
  2: 'bg-[#FBBF24]/25 text-[#FDE68A]',
  3: 'bg-[#F87171]/25 text-[#FECACA]',
};

/** Teinte de la SKILLCARD (`cardPalette`) : fond + accent des lignes d'exercice. */
export interface CardExercisePalette {
  accent: string;
  bg: string;
}

/** Accès SKILLCARD de la carte, tel que l'utilise la liste des exercices. */
export interface CardExerciseListAccess {
  allowed: boolean;
  /** Plus utilisé depuis 0690 (questions gratuites d'un exercice non découpé). */
  freeQuestions: number;
  /** Le seul exercice gratuit de la carte (0690) : `skillcard_free_exercise_id`. */
  freeExerciseId?: string | null;
}

/**
 * État d'un exercice dans la liste (migrations 0501, 0690). PUR, testé.
 *  - `open`   : accès complet à la carte (ou accès encore inconnu) ;
 *  - `free`   : LE seul exercice gratuit de la carte (son essai désigné) ;
 *  - `locked` : tout le reste sans accès → verrouillé d'emblée (page d'achat).
 */
export type CardExerciseLock = { kind: 'open' } | { kind: 'free' } | { kind: 'locked' };

export function exerciseLockState(
  exercise: { id?: string; split_part?: number | null },
  access: CardExerciseListAccess | undefined,
): CardExerciseLock {
  if (access?.allowed || !access) return { kind: 'open' };
  if (exercise.id != null && exercise.id === access.freeExerciseId) return { kind: 'free' };
  return { kind: 'locked' };
}

/** Un essai verrouillé (0690) perd son 🎁 dans la liste : seul l'essai gratuit le garde. */
export function exerciseListTitle(title: string, lock: CardExerciseLock): string {
  return lock.kind === 'locked' ? title.replace(/\s*🎁\s*$/u, '') : title;
}

/**
 * Suite de la phrase « Exercice « … » créé » après une génération : découpé en
 * essai gratuit + 3 parties (migration 0501), ou gardé entier quand ses
 * questions sont liées. Rien pour un résultat sauté ou une ancienne réponse.
 */
export function generatedSplitNote(result: Pick<CardExerciseResult, 'split' | 'skipped'>): string {
  if (result.skipped || result.split === undefined) return '';
  if (result.split) return `découpé en essai gratuit + ${result.split.parts.length} parties`;
  return 'gardé entier (questions liées)';
}

/**
 * Libellé des questions d'un problème en situation (migration 0721) : « 9 questions · 2 sur cette
 * carte », ou seulement les questions reliées si le total est inconnu. PUR, testé.
 */
export function problemQuestionsLabel(total: number | null | undefined, linked: number): string {
  const onCard = `${linked} sur cette carte`;
  if (!total || total <= linked)
    return `${linked} question${linked > 1 ? 's' : ''} sur cette carte`;
  return `${total} questions · ${onCard}`;
}

/** « 3 problèmes » : la section « En situation » repliée n'affiche que leur nombre. PUR, testé. */
export function problemsCountLabel(n: number): string {
  return `${n} problème${n > 1 ? 's' : ''}`;
}

/** Nom court d'une partie d'exercice découpé (migration 0501), sinon null. */
export function splitPartLabel(splitPart: number | null | undefined): string | null {
  if (splitPart === 0) return 'Essai gratuit';
  if (splitPart != null && splitPart >= 1 && splitPart <= 3) return `Partie ${splitPart}`;
  return null;
}

export function CardExerciseList({
  exercises,
  questionXp,
  xpMax,
  currentXp,
  studentLevel,
  palette,
  notes,
  onOpen,
  access,
  cardId,
  attempts,
}: {
  exercises: PublicCardExercise[];
  questionXp: number;
  xpMax: number;
  /** XP déjà gagnée (élève connecté) ; absent = visiteur. */
  currentXp?: number;
  /** Niveau de l'élève : on ne montre que les exercices de son niveau ou inférieurs. */
  studentLevel?: string | null;
  /** Teinte de la carte : les lignes en prennent le fond et l'accent. */
  palette?: CardExercisePalette;
  /** Texte d'aide affiché sous les exercices (avant le barème HORA). */
  notes?: ReactNode;
  /** Appelé au clic sur un exercice (visiteur : mémoriser le retour à la carte). */
  onOpen?: () => void;
  /**
   * Accès SKILLCARD de la carte (migrations 0304, 0501, 0690). Absent = inconnu
   * (chargement) : rien n'est signalé. `allowed` = tout est ouvert ; sinon seul
   * l'essai gratuit de la carte l'est, le reste est verrouillé (lien vers l'achat).
   */
  access?: CardExerciseListAccess;
  /** Carte : les parties verrouillées mènent à sa page d'achat. */
  cardId?: string;
  /** Essais de l'élève connecté, par exercice (fait N fois, à continuer). */
  attempts?: Record<string, ExerciseAttemptSummary>;
}) {
  const themed = !!palette;
  // Couleurs de la carte en variables CSS (seules valeurs non statiques).
  const themeVars = palette
    ? ({ '--jr-ex-accent': palette.accent, '--jr-ex-bg': palette.bg } as CSSProperties)
    : undefined;
  const problemsListId = useId();
  const [problemsOpen, setProblemsOpen] = useState(false);
  const shown = exercisesForStudentLevel(exercises, studentLevel);
  const hidden = exercises.length - shown.length;
  const { drills, problems } = splitCardExercises(shown);
  const renderRow = (e: PublicCardExercise, variant: 'drill' | 'problem') => {
    const problem = variant === 'problem';
    // Teinte de la carte pour ses propres exercices ; un problème garde les couleurs de la page
    // (encre qui s'inverse en sombre) : il se distingue d'un coup d'œil des tuiles de la carte.
    const tone = themed && !problem;
    // Un problème en situation : chaque question porte sa propre difficulté, pas de pastille d'exercice.
    const badge = problem ? null : exerciseDifficultyBadge(e.difficulty);
    const potential = exerciseXpPotential({
      linkedQuestions: e.linked_questions,
      linkedWeight: e.linked_weight,
      questionXp,
      xpMax,
      currentXp,
    });
    const lock = exerciseLockState(e, access);
    const progress = exerciseAttemptBadge(attempts?.[e.id]);
    // Terminé en entier aujourd'hui (0660) : ne se refait que demain.
    const doneToday = !!attempts?.[e.id]?.doneToday && lock.kind !== 'locked';
    const questions = problem
      ? problemQuestionsLabel(e.question_count, e.linked_questions)
      : `${e.linked_questions} question${e.linked_questions > 1 ? 's' : ''}`;
    // Le titre porte ses pastilles (🟢🟠🔴 niveau, 🎁 / 1️⃣2️⃣3️⃣ partie,
    // migration 0543) : les étiquettes ne répètent ni « Partie N » ni
    // « Essai gratuit » ni « Niveau N ».
    // Libellé déjà complet (partie d'un exercice découpé, ou problème : « 9 questions · 2 sur cette carte »)
    const split = e.split_part != null || problem;
    const title = exerciseListTitle(e.title, lock);
    const rowClass = cn(
      'flex min-h-[2.75rem] items-center gap-3 rounded-xl border p-3 active:scale-[0.99]',
      problem
        ? tone
          ? 'border-dashed border-white/35 bg-white/[0.06] hover:bg-white/10'
          : 'border-ink-300 hover:bg-ink-50 border-dashed bg-white'
        : tone
          ? 'border-[color-mix(in_srgb,var(--jr-ex-accent)_55%,transparent)] bg-[linear-gradient(135deg,var(--jr-ex-bg),color-mix(in_srgb,var(--jr-ex-bg)_78%,var(--jr-ex-accent)))] shadow-md hover:brightness-110'
          : 'border-ink-200 hover:bg-ink-50',
      lock.kind === 'locked' && 'opacity-90',
    );
    const main = (
      <span className="min-w-0 flex-1">
        {problem && (
          <span
            data-testid="problem-kicker"
            className={cn(
              'block text-[11px] font-bold uppercase tracking-wide',
              tone ? 'text-[var(--jr-ex-accent)]' : 'text-primary-600',
            )}
          >
            {e.theme ? `Problème · ${e.theme}` : 'Problème'}
          </span>
        )}
        <span
          className={cn(
            'line-clamp-2 block text-sm font-semibold',
            tone ? 'text-white' : 'text-ink-900',
          )}
        >
          <MathText>{title}</MathText>
        </span>
        <span
          className={cn(
            'flex flex-wrap items-center gap-x-2 gap-y-1 text-xs',
            tone ? 'text-white/75' : 'text-ink-500',
          )}
        >
          {progress && (
            <span
              data-progress={progress.kind}
              className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold',
                progress.kind === 'continue'
                  ? tone
                    ? 'bg-[#60A5FA]/30 text-[#DBEAFE]'
                    : 'bg-primary-100 text-primary-700'
                  : tone
                    ? 'bg-[#34D399]/25 text-[#A7F3D0]'
                    : 'bg-xp-100 text-xp-700',
              )}
            >
              {progress.kind === 'continue' ? (
                <RotateCcw className="h-3 w-3" aria-hidden />
              ) : (
                <Check className="h-3 w-3" aria-hidden />
              )}
              {progress.text}
            </span>
          )}
          {lock.kind === 'free' ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold',
                tone ? 'bg-[#34D399]/25 text-[#A7F3D0]' : 'bg-xp-100 text-xp-700',
              )}
            >
              {split ? questions : `Gratuit · ${questions}`}
            </span>
          ) : lock.kind === 'locked' ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold',
                tone ? 'bg-white/15 text-white' : 'bg-ink-100 text-ink-700',
              )}
            >
              <Lock className="h-3 w-3" aria-hidden />
              {split ? questions : `À débloquer · ${questions}`}
            </span>
          ) : (
            <span>
              {split ? questions : `${questions} reliée${e.linked_questions > 1 ? 's' : ''}`}
            </span>
          )}
          {e.level && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 font-semibold',
                tone
                  ? 'bg-[color-mix(in_srgb,var(--jr-ex-accent)_30%,transparent)] text-white'
                  : 'bg-primary-50 text-primary-700',
              )}
            >
              {LEVEL_LABELS[e.level] ?? e.level}
            </span>
          )}
          {problem && potential > 0 && (
            <span className="text-primary-700 bg-primary-50 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold">
              jusqu’à +{potential} HORA
            </span>
          )}
          {badge && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 font-semibold',
                (tone ? THEMED_DIFFICULTY_TONES : DIFFICULTY_TONES)[badge.level],
              )}
              title={`${badge.label} : chaque bonne réponse rapporte ${badge.multiplier} HORA`}
            >
              {badge.multiplier} HORA
            </span>
          )}
        </span>
        {doneToday && (
          <span
            data-testid="exercise-done-today"
            className={cn(
              'mt-1.5 flex items-center gap-1 text-xs font-semibold',
              tone ? 'text-[#A7F3D0]' : 'text-xp-700',
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Déjà fait aujourd’hui · reviens
            demain
          </span>
        )}
      </span>
    );
    const content = (
      <>
        {problem && (
          <span
            aria-hidden
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg',
              tone ? 'bg-white/10 text-white/70' : 'bg-ink-100 text-ink-400',
            )}
          >
            {e.illustration_url ? (
              <img
                src={e.illustration_url}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <MapIcon className="h-6 w-6" />
            )}
          </span>
        )}
        {main}
        <span className={cn('shrink-0 text-right', problem && 'hidden')}>
          <span
            className={cn(
              'font-display block text-sm font-bold',
              tone ? 'text-[var(--jr-ex-accent)]' : 'text-ink-900',
            )}
          >
            {potential > 0 ? `+${potential} HORA` : 'Carte complète'}
          </span>
          {potential > 0 && (
            <span className={cn('text-[11px]', tone ? 'text-white/60' : 'text-ink-400')}>
              jusqu’à
            </span>
          )}
        </span>
        <span
          aria-label={
            lock.kind === 'locked'
              ? 'Débloquer'
              : doneToday
                ? 'Déjà fait aujourd’hui'
                : progress?.kind === 'continue'
                  ? 'Continuer'
                  : progress
                    ? 'Refaire'
                    : 'Commencer'
          }
          data-paid={lock.kind === 'locked' || undefined}
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            // Partie payante : couleurs inversées + bordure, pour la
            // distinguer d'un coup d'œil d'un exercice jouable.
            lock.kind === 'locked'
              ? tone
                ? 'border-2 border-[var(--jr-ex-accent)] bg-[var(--jr-ex-bg)] text-[var(--jr-ex-accent)] shadow-lg'
                : 'border-primary-500 text-primary-500 border-2 bg-white'
              : doneToday
                ? tone
                  ? 'bg-[#34D399]/25 text-[#A7F3D0]'
                  : 'bg-xp-100 text-xp-700'
                : tone
                  ? 'bg-[var(--jr-ex-accent)] text-[var(--jr-ex-bg)] shadow-lg'
                  : 'bg-primary-500 text-white',
          )}
        >
          {lock.kind === 'locked' ? (
            <Lock className="h-5 w-5" aria-hidden />
          ) : doneToday ? (
            <Check className="h-5 w-5" aria-hidden />
          ) : (
            <Play className="h-5 w-5 translate-x-px" aria-hidden />
          )}
        </span>
      </>
    );
    return (
      <li key={e.id}>
        {lock.kind === 'locked' && cardId ? (
          // Partie payante sans accès : verrouillée d'emblée → page d'achat.
          <a
            href={skillcardBuyPath(cardId, `/card/${cardId}`)}
            onClick={onOpen}
            style={themeVars}
            data-themed={tone || undefined}
            data-locked
            className={rowClass}
          >
            {content}
          </a>
        ) : doneToday ? (
          // Déjà fait aujourd'hui : plus cliquable (le serveur refuse aussi).
          <div
            aria-disabled="true"
            style={themeVars}
            data-themed={tone || undefined}
            data-done-today
            className={cn(rowClass, 'cursor-default opacity-80 active:scale-100')}
          >
            {content}
          </div>
        ) : (
          <Link
            to="/learn/exercise/$exercise"
            params={{ exercise: e.id }}
            onClick={onOpen}
            style={themeVars}
            data-themed={tone || undefined}
            className={rowClass}
          >
            {content}
          </Link>
        )}
      </li>
    );
  };
  return (
    <div className="space-y-2">
      {shown.length === 0 ? (
        <p className="text-ink-500 text-sm">
          {hidden > 0
            ? 'Les exercices de cette carte sont d’un niveau supérieur au tien.'
            : 'Aucun exercice relié pour le moment.'}
        </p>
      ) : (
        drills.length > 0 && (
          <ul className="space-y-2" aria-label="Exercices pour gagner de l’HORA">
            {drills.map((e) => renderRow(e, 'drill'))}
          </ul>
        )
      )}
      {problems.length > 0 && (
        // Repliée par défaut (Philippe, 30/10) : seulement le nombre de problèmes ; on touche pour les voir.
        <section className="space-y-2 pt-2">
          <h3 className="m-0">
            <button
              type="button"
              aria-expanded={problemsOpen}
              aria-controls={problemsListId}
              onClick={() => setProblemsOpen((o) => !o)}
              className="border-ink-200 hover:bg-ink-50 flex min-h-[2.75rem] w-full items-center gap-2 rounded-xl border border-dashed px-3 text-left"
            >
              <MapIcon className="text-ink-500 h-4 w-4 shrink-0" aria-hidden />
              <span className="text-ink-900 flex-1 text-sm font-bold">En situation</span>
              <span className="bg-primary-50 text-primary-700 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold">
                {problemsCountLabel(problems.length)}
              </span>
              <ChevronDown
                className={cn(
                  'text-ink-500 h-4 w-4 shrink-0 transition-transform',
                  problemsOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
          </h3>
          {problemsOpen && (
            <div id={problemsListId} className="space-y-2">
              <p className="text-ink-500 text-xs">
                Des problèmes concrets où cette carte sert, parmi d’autres.
              </p>
              <ul className="space-y-2" aria-label="Problèmes en situation">
                {problems.map((e) => renderRow(e, 'problem'))}
              </ul>
            </div>
          )}
        </section>
      )}
      {/* Les explications viennent APRÈS les exercices : la liste suit directement la rangée HORA · avatar · maîtrise. */}
      <div className="space-y-1 pt-1">
        {shown.length > 0 && hidden > 0 && (
          <p className="text-ink-400 text-xs">
            {hidden} exercice{hidden > 1 ? 's' : ''} d’un niveau supérieur au tien masqué
            {hidden > 1 ? 's' : ''}.
          </p>
        )}
        {notes}
        <p className="text-ink-500 text-xs">
          Chaque bonne réponse à une question reliée rapporte {questionXp} HORA à la carte (×1,5 si
          la question est moyenne, ×2 si elle est difficile).
        </p>
      </div>
    </div>
  );
}

/**
 * Admin : générer les exercices de la carte — un seul (Niveau 1 standard,
 * Niveau 2 ×1,5 HORA ou Niveau 3 ×2) ou les 3 niveaux D'UN COUP (en parallèle,
 * niveaux déjà présents sautés). Brouillon à relire, sauf « Publier directement ».
 */
const STATUS_TONES: Record<string, string> = {
  draft: 'bg-amber-50 text-amber-700',
  awaiting_review: 'bg-amber-50 text-amber-700',
  archived: 'bg-ink-100 text-ink-600',
};

/**
 * Admin : exercices reliés à la carte qui ne sont PAS publiés (brouillons, à
 * relire, archivés) — invisibles dans la liste publique. Gérer (page de
 * l'exercice : relire, modifier, tester) ou publier en un clic.
 */
export function CardAdminExercises({ cardId }: { cardId: string }) {
  const list = useCardAdminExercises(cardId, true);
  const setStatus = useSetCardExerciseStatus(cardId);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const publish = async (id: string) => {
    setError(null);
    setPendingId(id);
    try {
      await setStatus.mutateAsync({ exerciseId: id, status: 'published' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publication impossible.');
    } finally {
      setPendingId(null);
    }
  };

  if (list.isLoading) return null;
  if (list.error) {
    return (
      <p role="alert" className="text-error-600 text-xs">
        Impossible de charger les brouillons de la carte.
      </p>
    );
  }
  const items = list.data ?? [];
  return (
    <div className="space-y-2" data-testid="card-admin-exercises">
      <p className="text-ink-700 text-sm font-semibold">
        Non publiés{items.length > 0 ? ` · ${items.length}` : ''}
      </p>
      {items.length === 0 ? (
        <p className="text-ink-500 text-xs">
          Aucun brouillon : tous les exercices reliés sont publiés.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((e) => {
            const tier = exerciseDifficultyBadge(e.difficulty);
            const canPublish = e.status === 'draft' || e.status === 'awaiting_review';
            return (
              <li
                key={e.id}
                className="border-ink-100 flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 text-sm font-semibold">
                    <MathText>{e.title}</MathText>
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    <span
                      className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_TONES[e.status] ?? 'bg-ink-100 text-ink-600'}`}
                    >
                      {CARD_ADMIN_STATUS_LABELS[e.status] ?? e.status}
                    </span>
                    {tier && (
                      <span className={`rounded-full px-2 py-0.5 ${DIFFICULTY_TONES[tier.level]}`}>
                        {tier.label}
                      </span>
                    )}
                    {e.level && (
                      <span className="bg-primary-50 text-primary-700 rounded-full px-2 py-0.5">
                        {LEVEL_LABELS[e.level] ?? e.level}
                      </span>
                    )}
                    <span className="text-ink-500">
                      {e.linkedQuestions} question{e.linkedQuestions > 1 ? 's' : ''} reliée
                      {e.linkedQuestions > 1 ? 's' : ''}
                    </span>
                    {splitPartLabel(e.splitPart) && (
                      <span className="bg-ink-100 text-ink-700 rounded-full px-2 py-0.5">
                        {splitPartLabel(e.splitPart)}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Link
                    to="/tutor/exercises/$exerciseId"
                    params={{ exerciseId: e.id }}
                    className="border-ink-200 text-ink-700 hover:border-primary-300 inline-flex min-h-[2.75rem] items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-semibold active:scale-[0.98]"
                  >
                    <Pencil className="h-4 w-4" aria-hidden /> Gérer
                  </Link>
                  {canPublish && (
                    <Button
                      size="sm"
                      fullWidth
                      className="min-h-[2.75rem] sm:w-auto"
                      loading={pendingId === e.id}
                      disabled={pendingId !== null}
                      onClick={() => void publish(e.id)}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        <Send className="h-4 w-4" aria-hidden />
                        {e.splitPart === 0 ? 'Publier avec ses 3 parties' : 'Publier'}
                      </span>
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-error-600 text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

export function CardExerciseGenerator({ cardId, levels }: { cardId: string; levels: string[] }) {
  const generate = useGenerateCardExercise();
  const generateSet = useGenerateCardExerciseSet();
  const [level, setLevel] = useState(levels.length === 1 ? levels[0]! : '');
  const [model, setModel] = useState<PipelineModelId>(CARD_DEFAULT_MODEL);
  const [multipleAnswers, setMultipleAnswers] = useState(true);
  const [difficulty, setDifficulty] = useState<'' | 'medium' | 'hard'>('');
  const [publish, setPublish] = useState(false);
  const [result, setResult] = useState<CardExerciseResult | null>(null);
  const [setResults, setSetResults] = useState<CardExerciseSetItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** feuilles de brouillon en cours, par exercice (niveau, ou « one ») */
  const [sheets, setSheets] = useState<Record<string, CardExerciseSheetProgress>>({});
  const busy = generate.isPending || generateSet.isPending;
  const sheetTotals = Object.values(sheets).reduce(
    (a, p) => ({ done: a.done + p.done, total: a.total + p.total }),
    { done: 0, total: 0 },
  );
  const common = () => ({
    card_id: cardId,
    level: level || undefined,
    model,
    multiple_answers: multipleAnswers,
    publish,
  });

  const run = async () => {
    setError(null);
    setResult(null);
    setSetResults(null);
    setSheets({});
    try {
      setResult(
        await generate.mutateAsync({
          ...common(),
          difficulty: difficulty || undefined,
          onSheetProgress: (p) => setSheets({ one: p }),
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Génération impossible.');
    }
  };

  const runAll = async () => {
    setError(null);
    setResult(null);
    setSetResults(null);
    setSheets({});
    try {
      setSetResults(
        await generateSet.mutateAsync({
          ...common(),
          onSheetProgress: (tier, p) => setSheets((all) => ({ ...all, [tier]: p })),
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Génération impossible.');
    }
  };

  return (
    <div className="border-primary-200 space-y-3 rounded-xl border border-dashed p-3">
      <p className="text-ink-700 text-sm font-semibold">Admin — exercices de la carte</p>
      <CardAdminExercises cardId={cardId} />
      <p className="text-ink-500 text-xs">
        10 questions QCM sur cette carte, exercice gratuit (style Warmup). Niveau 1 = standard
        progressif, Niveau 2 = moyen (×1,5 HORA), Niveau 3 = difficile (×2 HORA).
      </p>
      {levels.length > 1 && (
        <label className="block space-y-1">
          <span className="text-ink-600 text-xs font-semibold">Niveau de l’exercice</span>
          <select
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            disabled={busy}
          >
            <option value="">Choisis le niveau…</option>
            {levels.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABELS[l] ?? l}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="flex min-h-[2.75rem] items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={multipleAnswers}
          onChange={(e) => setMultipleAnswers(e.target.checked)}
          disabled={busy}
        />
        <span className="text-ink-700">
          Inclure des questions à plusieurs bonnes réponses (3 sur 10)
        </span>
      </label>
      <label className="flex min-h-[2.75rem] items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
          disabled={busy}
        />
        <span className="text-ink-700">Publier directement (sans relecture)</span>
      </label>
      <AiModelSelect value={model} onChange={setModel} disabled={busy} />

      <div className="border-ink-100 space-y-2 rounded-lg border p-3">
        <p className="text-ink-700 text-sm font-semibold">Les 3 niveaux d’un coup</p>
        <p className="text-ink-500 text-xs">
          Crée en parallèle les exercices Niveau 1, 2 et 3. Un niveau déjà présent sur la carte est
          sauté.
        </p>
        <Button
          fullWidth
          className="sm:w-auto"
          disabled={!level || busy}
          loading={generateSet.isPending}
          onClick={() => void runAll()}
        >
          <span className="flex items-center justify-center gap-2">
            <Layers className="h-4 w-4" aria-hidden /> Créer les 3 niveaux (IA)
          </span>
        </Button>
      </div>

      <div className="border-ink-100 space-y-2 rounded-lg border p-3">
        <label className="block space-y-1">
          <span className="text-ink-600 text-xs font-semibold">Difficulté</span>
          <select
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as '' | 'medium' | 'hard')}
            disabled={busy}
          >
            <option value="">Niveau 1 — standard, progressif</option>
            <option value="medium">Niveau 2 — moyen, ×1,5 HORA</option>
            <option value="hard">Niveau 3 — difficile, ×2 HORA</option>
          </select>
        </label>
        <Button
          fullWidth
          variant="secondary"
          className="sm:w-auto"
          disabled={!level || busy}
          loading={generate.isPending}
          onClick={() => void run()}
        >
          <span className="flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden /> Générer un exercice (IA)
          </span>
        </Button>
      </div>

      {busy && (
        <p role="status" className="text-ink-500 text-xs">
          {sheetTotals.total > 0
            ? `Feuilles de brouillon : ${sheetTotals.done} / ${sheetTotals.total} questions…`
            : generateSet.isPending
              ? 'L’IA écrit les 3 exercices en parallèle… (une à deux minutes)'
              : 'L’IA écrit les 10 questions… (jusqu’à une minute)'}
        </p>
      )}
      {result && (
        <p role="status" className="text-xp-700 text-sm">
          Exercice « {result.title} » {publish ? 'publié' : 'créé en brouillon'}
          {generatedSplitNote(result) && `, ${generatedSplitNote(result)}`}.{' '}
          <Link
            to="/tutor/exercises/$exerciseId"
            params={{ exerciseId: result.exercise_id }}
            className="font-semibold underline"
          >
            {publish ? 'L’ouvrir' : 'Le relire et le publier'}
          </Link>
          {result.sheets && (
            <span className="text-ink-600 mt-1 block text-xs">
              {autoSheetSummary(result.sheets)}
            </span>
          )}
        </p>
      )}
      {setResults && (
        <ul role="status" className="space-y-1 text-sm">
          {setResults.map((item) => (
            <li key={item.tier} className={item.ok ? 'text-ink-700' : 'text-error-600'}>
              <span className="font-semibold">{CARD_EXERCISE_TIER_LABELS[item.tier]}</span> —{' '}
              {!item.ok ? (
                item.error
              ) : item.result.skipped ? (
                'déjà présent, rien créé'
              ) : (
                <>
                  {publish ? 'publié' : 'brouillon créé'}
                  {generatedSplitNote(item.result) && `, ${generatedSplitNote(item.result)}`} ·{' '}
                  <Link
                    to="/tutor/exercises/$exerciseId"
                    params={{ exerciseId: item.result.exercise_id }}
                    className="font-semibold underline"
                  >
                    {item.result.title ?? 'ouvrir'}
                  </Link>
                  {item.result.sheets && (
                    <span className="text-ink-600 block text-xs">
                      {autoSheetSummary(item.result.sheets)}
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </div>
  );
}
